import app from "@/main";
import {listeCevabi, temizParametreler} from "@/services/apiYardimcilari";

/**
 * Zamanlanmış senkron planlarının CRUD ve sabah raporu uçları
 * (Sorgu Snapshot Faz 3, DEV-2962…2965 backend / DEV-2966…2968 istemci).
 *
 * Uçlar kiracı (tenant) veritabanına bakar; `Authorization` / `Tenant-Id` /
 * `TCKN` başlıklarını `plugins/axios.ts` request interceptor'ı otomatik ekler,
 * burada elle başlık yazılmaz. Aynı interceptor'ın yanıt tarafı `response.data`
 * döndürdüğü için `$http.get(...)` doğrudan gövdeye çözülür.
 *
 * Desen `sorguOneriService.ts`'ten alındı (Faz 2): sınıf + singleton export.
 * `listeCevabi` toleransı ve `temizParametreler` süzgeci artık ortak
 * `@/services/apiYardimcilari` modülünden geliyor (kod inceleme: iki serviste
 * birebir kopya kaldı, ortak modüle taşındı).
 */

/** Plandaki tek alan: picker `name` + kaç günde bir koşacağı. */
export interface SenkronPlanAlan {
    /** `TakipSorguAlanlariPicker`ın `name` değeri — sunucuda beyaz listeli. */
    name: string;
    /** 1 = her gece. Sunucu sınırı [1, 365]. */
    frekans_gun: number;
}

/**
 * Plan dosyası. `dosyaId` SAKLANMAZ: UYAP'ta volatil ve oturumdan oturuma
 * değişiyor. Anahtar `birim_id + dosya_esas_no`, yani `sorgu_snapshot.dosya_ref`
 * ("<birimId>:<dosyaEsasNo>") ile aynı ikili.
 */
export interface SenkronPlanDosya {
    birim_id: string;
    dosya_esas_no: string;
    birim_adi?: string;
}

export interface SenkronPlanDosyaSecimi {
    /** v1'de tek kabul edilen değer; `filtre` F4+ için yer tutuyor. */
    tip: "liste";
    dosyalar: Array<SenkronPlanDosya>;
}

export interface SenkronPlan {
    id: number;
    ad: string;
    aktif: boolean;
    /** MySQL TIME kolonu: cevapta "HH:MM:SS" gelir. Bkz. `saatiEkranBicimine`. */
    kosum_saati: string;
    ucretli_sorguya_devam: boolean;
    alanlar: Array<SenkronPlanAlan>;
    dosya_secimi: SenkronPlanDosyaSecimi;
    /** Sunucu yönetir; PATCH bu alana dokunmaz. "YYYY-AA-GG" ya da null. */
    son_uretim_gunu: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * POST/PATCH tetiğinin makine tarafı (`SenkronPlanIsleyici::SEBEP_*`).
 * İstemci bu koddan KENDİ cümlesini kurmaz — kullanıcıya `mesaj` gösterilir;
 * `sebep` yalnız ikon/renk seçimi içindir.
 */
export type SenkronPlanSebep =
    | "uretildi"
    | "plan_pasif"
    | "saat_gecti"
    | "zaten_uretildi"
    | "uretim_hatasi"
    | "degisiklik_yok"
    | "bugun_uretilmedi"
    | "telafi_bekliyor"
    | "kosum_basladi"
    | "yeniden_uretildi"
    | "iptal_edildi"
    | "paket_bulunamadi"
    | "eszamanli_islem";

/**
 * POST/PATCH cevabı: plan nesnesinin ÜZERİNE tetik sonucu eklenir.
 *
 * `mesaj` sunucuda üretilen Türkçe cümledir ve kullanıcıya AYNEN gösterilir:
 * "kaydettim, neden koşmadı" sürprizini önleyen cümle, kararı veren yerle aynı
 * yerde durmalı (bkz. docs/sorgu-snapshot-sistemi.md §3, cevap sözleşmesi).
 */
export interface SenkronPlanYazmaCevabi extends SenkronPlan {
    /** Yalnız POST cevabında. */
    uretildi?: boolean;
    /** Yalnız PATCH cevabında. */
    yeniden_uretildi?: boolean;
    // kod inceleme: iptal_edilen/ilk_kosum/uretilen_gorev sunucu cevabında
    // geliyor ama hiçbir tüketici (SenkronPlanDialog/Listesi.vue, SenkronPlanView.vue)
    // okumuyordu; tip gerçekte kullanılan şekli yansıtsın diye kaldırıldı.
    hata: string | null;
    sebep: SenkronPlanSebep;
    mesaj: string;
}

/**
 * Yazma gövdesi. TÜM ALANLAR OPSİYONELDİR ve gönderilmeyen anahtar PATCH'te
 * DEĞİŞMEZ.
 *
 * DİKKAT: gövdedeki `null` "gönderilmedi" demektir, "sıfırla" değil. Bu yüzden
 * bu tipe hiçbir zaman `null` yazılmaz; atlanacak alan `undefined` bırakılır ve
 * `JSON.stringify` anahtarı gövdeden tamamen düşürür. Gövde kurulumu
 * `senkronPlanGovde.ts`'te merkezileştirildi.
 */
export interface SenkronPlanGovde {
    ad?: string;
    aktif?: boolean;
    kosum_saati?: string;
    ucretli_sorguya_devam?: boolean;
    alanlar?: Array<SenkronPlanAlan>;
    dosya_secimi?: SenkronPlanDosyaSecimi;
}

/** Sabah raporunun plan başına tek satırı. */
export interface SenkronRaporSatiri {
    plan_id: number;
    /** Plan silinmiş olabilir; koşum özeti kalıcıdır, o hâlde `null` gelir. */
    ad: string | null;
    /** O gece üretilen çocuk görev sayısı. */
    uretilen_gorev: number;
    /** Frekans bekçisinin düşürdüğü dosya sayısı. */
    atlanan_dosya: number;
    /** BITTI_BASARILI */
    biten: number;
    /** BITTI_HATALI + BITTI_EKSIK + IPTAL_EDILDI */
    hatali: number;
    /** SIRADA + ISLENIYOR + BEKLEMEDE + DURAKLATILDI + YENIDEN_BASLATILDI */
    bekleyen: number;
    /** O gün açılan haciz önerileri, planın dosyalarıyla sınırlı. */
    yeni_oneri: number;
    yuksek_oncelikli_oneri: number;
    // kod inceleme: ana_gorev_id sunucu cevabında geliyor ama ne
    // SenkronRaporGecmisi.vue'da ne senkronRaporOzeti.ts'te ne de uyap-plugin'deki
    // senkronSabahRaporuMesaji.ts'te okunuyor (yalnız test fixture'larında vardı);
    // tip gerçek kullanımı yansıtsın diye kaldırıldı.
    /** Üretim çöktüyse sebebi; yoksa null. */
    hata: string | null;
}

export interface SenkronRaporCevap {
    gun: string;
    planlar: Array<SenkronRaporSatiri>;
}

/** Rapor ucu tanınmayan bir şey döndürürse ekran boş listeyle çalışabilsin. */
function raporCevabi(cevap: any, istenenGun: string | undefined): SenkronRaporCevap {
    if (cevap && Array.isArray(cevap.planlar)) {
        return {gun: String(cevap.gun ?? istenenGun ?? ""), planlar: cevap.planlar};
    }
    return {gun: istenenGun ?? "", planlar: []};
}

class SenkronPlanService {
    /**
     * `GET /api/v1/senkron-plan` — kiracının tüm planları, PASİFLER DAHİL, id sırasında.
     * Sayfalama yok: plan sayısı doğası gereği tek hanelidir.
     */
    async listele(): Promise<SenkronPlan[]> {
        const cevap = await app.$http.get("/api/v1/senkron-plan");
        return listeCevabi<SenkronPlan>(cevap);
    }

    /**
     * `POST /api/v1/senkron-plan`
     *
     * Üretim hata alsa bile 201 döner (plan KAYDEDİLDİ); hata `hata` alanında
     * gelir. Çağıran taraf `mesaj`ı her hâlükârda göstermeli.
     */
    async olustur(govde: SenkronPlanGovde): Promise<SenkronPlanYazmaCevabi> {
        return await app.$http.post("/api/v1/senkron-plan", govde);
    }

    /**
     * `PATCH /api/v1/senkron-plan/{id}` — kısmi güncelleme.
     *
     * Gövdede olmayan anahtar değişmez; bu yüzden yalnız `{aktif}` göndermek
     * geçerli ve tercih edilen yoldur (bkz. `aktifDegistir`).
     */
    async guncelle(id: number, govde: SenkronPlanGovde): Promise<SenkronPlanYazmaCevabi> {
        return await app.$http.patch(`/api/v1/senkron-plan/${id}`, govde);
    }

    /**
     * Yalnız `aktif` alanını gönderir.
     *
     * Satırın tamamı yayılsaydı, ekranda duran bayat bir `alanlar`/`dosya_secimi`
     * kopyası başka bir pencerede yapılmış değişikliği ezerdi; ayrıca içerik aynı
     * olsa bile anahtar sırası farklıysa sunucu paketi boşuna iptal edip yeniden
     * üretirdi.
     */
    async aktifDegistir(id: number, aktif: boolean): Promise<SenkronPlanYazmaCevabi> {
        return await app.$http.patch(`/api/v1/senkron-plan/${id}`, {aktif});
    }

    /** `DELETE /api/v1/senkron-plan/{id}` — koşum geçmişi (`senkron_kosum`) silinmez. */
    async sil(id: number): Promise<{ message: string }> {
        return await app.$http.delete(`/api/v1/senkron-plan/${id}`);
    }

    /**
     * `GET /api/v1/senkron-rapor?gun=YYYY-AA-GG` — sabah raporunun tek kaynağı.
     * `gun` verilmezse uç bugünü kullanır. Biçim `Y-m-d` değilse uç 422 döner.
     */
    async rapor(gun?: string): Promise<SenkronRaporCevap> {
        const cevap = await app.$http.get("/api/v1/senkron-rapor", {
            params: temizParametreler({gun})
        });
        return raporCevabi(cevap, gun);
    }
}

export default new SenkronPlanService();
